import React, { useEffect, useState } from "react";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import { MapContainer, TileLayer, Popup } from "react-leaflet";
import { FaTree, FaMapMarkerAlt, FaCalendarPlus, FaCalendarAlt, FaClock, FaPagelines, FaUserAlt, FaFilePdf } from 'react-icons/fa';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "leaflet/dist/leaflet.css";
import "../assets/NewFiles/Css/ProfilePage2.css";
import "../assets/NewFiles/Css/ProjectPage.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaCheckCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import api from "../axios/api";
import ReactLoading from "react-loading";
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
} from "react-share";
import {
  EmailIcon,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon
} from "react-share";
import { FaInstagram } from 'react-icons/fa';
import MapToggle from "../components/common/MapToggle";

const verifiedIcon = new L.Icon({
  iconUrl: "/images/icon/leaf.png",
  iconSize: [40, 40],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const unverifiedIcon = new L.Icon({
  iconUrl: "/images/icon/leaf.png",
  iconSize: [40, 40],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Default fallback data
const defaultPosition = [20.5937, 78.9629]; // India center coordinates
const DEFAULT_WOOD_DENSITY = 0.5;
const DEFAULT_FORM_FACTOR = 0.5;
const DBH_GROWTH_PER_MONTH = 0.05 / 100; // 0.05 cm/month → metres
const SEEDLING_DBH = 0.005;       // 0.5 cm starting DBH in metres

const calcCO2eFromDBH = (dbh_meters, wd = DEFAULT_WOOD_DENSITY, ff = DEFAULT_FORM_FACTOR) => {
  try {
    if (!dbh_meters || isNaN(dbh_meters) || parseFloat(dbh_meters) <= 0) return 0;
    const D = parseFloat(dbh_meters) * 100;
    const WD = (wd && !isNaN(wd) && parseFloat(wd) > 0) ? parseFloat(wd) : DEFAULT_WOOD_DENSITY;
    const FF = (ff && !isNaN(ff) && parseFloat(ff) > 0) ? parseFloat(ff) : DEFAULT_FORM_FACTOR;
    const lnD = Math.log(D);
    const AGB = FF * WD * Math.exp(
      -1.499 + 2.148 * lnD + 0.207 * lnD ** 2 - 0.0281 * lnD ** 3
    );
    return parseFloat((AGB * 0.47 * 3.67).toFixed(4));
  } catch {
    return 0;
  }
};

const getDefaultChartData = (label = "No Data") => ({
  labels: ["No Data"],
  datasets: [{
    label,
    data: [0],
    borderColor: "#e0e0e0",
    backgroundColor: "rgba(224, 224, 224, 0.1)",
    tension: 0.1
  }]
});

export default function ClientImpactDashboard() {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const co2ChartRef = React.useRef(null);
  const treesByMonthChartRef = React.useRef(null);
  const weeklyTreesChartRef = React.useRef(null);
  const plantationStatusChartRef = React.useRef(null);
  const totalTreesChartRef = React.useRef(null);

const getSpeciesLabel = (tree) => {
  if (!tree) return 'Unknown Species';
  const commonName = (tree.specie_common_name || tree.common_name || tree.species_common_name || '').trim();
  const scientificName = (tree.tree_species_label || tree.speciesLabel || tree.species_label || tree.label || '').trim();
  if (commonName && scientificName && commonName.toLowerCase() !== scientificName.toLowerCase()) {
    return `${commonName} / ${scientificName}`;
  }
  return commonName || scientificName || tree.treeName || (tree.tree_species ? `Species-${tree.tree_species}` : 'Unknown Species');
};

  const getSpeciesDistribution = (trees) => {
    if (!trees) return [];
    const dist = {};
    trees.forEach(tree => {
      const species = getSpeciesLabel(tree);
      dist[species] = (dist[species] || 0) + 1;
    });
    return Object.keys(dist).sort().map(key => [key, dist[key]]);
  };

  const getBase64ImageFromUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    let url = imageUrl;
    if (url.startsWith('/')) {
      url = window.location.origin + url;
    }
    const cacheBuster = (url.includes('?') ? '&' : '?') + '_cb=' + new Date().getTime();
    const fetchUrl = url + cacheBuster;
    try {
      const res = await fetch(fetchUrl, { method: 'GET', mode: 'cors' });
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Fetch failed for image base64, trying fallback:", url, error);
      return new Promise((resolve) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          try {
            resolve(canvas.toDataURL('image/jpeg'));
          } catch (err) {
            console.error("Canvas tainted for image:", url, err);
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = fetchUrl;
      });
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let currentY = 15;

      const addHeader = (titleText) => {
        doc.setFillColor(27, 67, 50);
        doc.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F');
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(titleText, margin + 5, currentY + 8);
        currentY += 18;
      };

      const checkPageBreak = (heightNeeded) => {
        if (currentY + heightNeeded > pageHeight - margin - 15) {
          doc.addPage();
          currentY = margin;
          return true;
        }
        return false;
      };

      // PAGE 1: TITLE & COVER IMAGE & PROFILE DETAILS
      doc.setFillColor(27, 67, 50);
      doc.rect(margin, currentY, pageWidth - 2 * margin, 25, 'F');

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(200, 220, 200);
      doc.text('ARANYASETU COMPANY CLIMATE IMPACT REPORT', margin + 6, currentY + 8);

      const clientName = companyData?.company_name || clientData?.full_name || "Client Report";
      const splitTitle = doc.splitTextToSize(`Client: ${clientName}`, pageWidth - 2 * margin - 12);
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(splitTitle, margin + 6, currentY + 16);
      currentY += 32;

      // Cover Pic
      if (companyData?.company_cover_pic) {
        const coverBase64 = await getBase64ImageFromUrl(companyData.company_cover_pic);
        if (coverBase64) {
          doc.addImage(coverBase64, 'JPEG', margin, currentY, pageWidth - 2 * margin, 65, undefined, 'FAST');
          doc.setDrawColor(27, 67, 50);
          doc.setLineWidth(0.3);
          doc.rect(margin, currentY, pageWidth - 2 * margin, 65);
          currentY += 72;
        }
      }

      // Profile details
      const profileData = [
        ['Company Name', companyData?.company_name || 'N/A'],
        ['Client Name', clientData?.full_name || 'N/A'],
        ['Industry', companyData?.industry || 'N/A'],
        ['Client Since', formatDateUs(clientData?.createdAt)]
      ];

      autoTable(doc, {
        startY: currentY,
        body: profileData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 0, textColor: [60, 60, 60], font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 45 },
          1: { width: 135 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // PAGE 2: PLANTATION SUMMARY & STATISTICS
      checkPageBreak(50);
      addHeader('Plantation Status & Summary');

      const plantationSummary = [
        ['Total Planted Trees', `${treeData.length || 0} Trees`],
        ['Verified Trees', `${treeStats?.verifiedTrees || 0} Trees`],
        ['Geolocated Trees', `${validTreesForMap.length || 0} Trees`],
        ['Average Weekly Trees', `${averageWeeklyCount} Trees`]
      ];

      autoTable(doc, {
        startY: currentY,
        body: plantationSummary,
        theme: 'striped',
        styles: { fontSize: 10, cellPadding: 0, font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 60 },
          1: { width: 120 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // Cumulative CO2 Offset & O2 Produced by Month (Only render if there is data)
      if (co2ChartResult) {
        checkPageBreak(95);
        addHeader('Total Cumulative CO2 Offset & O2 Produced by Month');

        const summaryText = `Total CO2e Stored: ${co2ChartResult.totalCO2e >= 1000 ? (co2ChartResult.totalCO2e / 1000).toFixed(2) + ' t' : co2ChartResult.totalCO2e + ' kg'}   |   Total O2 Produced: ${co2ChartResult.totalO2 >= 1000 ? (co2ChartResult.totalO2 / 1000).toFixed(2) + ' t' : co2ChartResult.totalO2 + ' kg'}   |   Trees with DBH: ${co2ChartResult.treesWithDBH} / ${co2ChartResult.totalTrees}`;
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(27, 67, 50);
        doc.text(summaryText, margin, currentY);
        currentY += 6;

        if (co2ChartRef.current) {
          try {
            const co2Img = co2ChartRef.current.toBase64Image();
            if (co2Img) {
              doc.addImage(co2Img, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture CO2 Chart:", e);
            const tableRows = co2ChartResult.labels.map((lbl, idx) => [lbl, `${co2ChartResult.data[idx]} kg`, `${co2ChartResult.o2Data[idx]} kg`]);
            autoTable(doc, {
              startY: currentY,
              head: [['Month', 'CO2e Absorbed', 'O2 Produced']],
              body: tableRows,
              margin: { left: margin, right: margin },
              styles: { fontSize: 8 },
              headStyles: { fillColor: [76, 175, 80] }
            });
            currentY = doc.lastAutoTable.finalY + 10;
          }
        } else {
          const tableRows = co2ChartResult.labels.map((lbl, idx) => [lbl, `${co2ChartResult.data[idx]} kg`, `${co2ChartResult.o2Data[idx]} kg`]);
          autoTable(doc, {
            startY: currentY,
            head: [['Month', 'CO2e Absorbed', 'O2 Produced']],
            body: tableRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [76, 175, 80] }
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
        currentY += 5;
      }

      // PAGE 3: CHARTS & SPECIES DISTRIBUTION
      // Trees by Month
      if (apiStats && apiStats.length > 0) {
        checkPageBreak(95);
        addHeader('Total Trees Registered by Month');

        if (treesByMonthChartRef.current) {
          try {
            const chartImg = treesByMonthChartRef.current.toBase64Image();
            if (chartImg) {
              doc.addImage(chartImg, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture Trees by Month Chart:", e);
            const tableRows = apiStats.map(stat => [stat.registration_month, stat.total_trees_registered]);
            autoTable(doc, {
              startY: currentY,
              head: [['Month', 'Trees Registered']],
              body: tableRows,
              margin: { left: margin, right: margin },
              styles: { fontSize: 8 },
              headStyles: { fillColor: [76, 175, 80] }
            });
            currentY = doc.lastAutoTable.finalY + 10;
          }
        } else {
          const tableRows = apiStats.map(stat => [stat.registration_month, stat.total_trees_registered]);
          autoTable(doc, {
            startY: currentY,
            head: [['Month', 'Trees Registered']],
            body: tableRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [76, 175, 80] }
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
        currentY += 5;
      }

      // Average Weekly Trees
      if (weeklyTreesData && weeklyTreesData.data.some(v => v > 0)) {
        checkPageBreak(95);
        addHeader('Average Weekly Trees (Last 7 Days)');

        if (weeklyTreesChartRef.current) {
          try {
            const chartImg = weeklyTreesChartRef.current.toBase64Image();
            if (chartImg) {
              doc.addImage(chartImg, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture Weekly Trees Chart:", e);
            const tableRows = weeklyTreesData.labels.map((lbl, idx) => [lbl, weeklyTreesData.data[idx]]);
            autoTable(doc, {
              startY: currentY,
              head: [['Day', 'Trees Planted']],
              body: tableRows,
              margin: { left: margin, right: margin },
              styles: { fontSize: 8 },
              headStyles: { fillColor: [76, 175, 80] }
            });
            currentY = doc.lastAutoTable.finalY + 10;
          }
        } else {
          const tableRows = weeklyTreesData.labels.map((lbl, idx) => [lbl, weeklyTreesData.data[idx]]);
          autoTable(doc, {
            startY: currentY,
            head: [['Day', 'Trees Planted']],
            body: tableRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [76, 175, 80] }
          });
          currentY = doc.lastAutoTable.finalY + 10;
        }
        currentY += 5;
      }

      // Species-wise Tree Distribution (replacing interactive map)
      const speciesRows = getSpeciesDistribution(treeData);
      if (speciesRows.length > 0) {
        checkPageBreak(50);
        addHeader('Species-wise Tree Distribution');

        autoTable(doc, {
          startY: currentY,
          head: [['Species Name', 'Tree Count']],
          body: speciesRows,
          theme: 'striped',
          styles: { fontSize: 9, font: 'Helvetica' },
          headStyles: { fillColor: [27, 67, 50], textColor: [255, 255, 255] },
          margin: { left: margin, right: margin }
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Footers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);

        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        doc.text(`Aranyasetu Client: ${clientName}`, margin, pageHeight - 8);

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);
      }

      const pdfFileName = `${(clientName).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_impact_report.pdf`;
      doc.save(pdfFileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const [treeLoading, setTreeLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [treeStats, setTreeStats] = useState(null);
  const [apiStats, setApiStats] = useState([]);
  const CO2_TO_O2_RATIO = 0.727;

  // Helper function to check if coordinates are valid
  const hasValidCoordinates = (lat, lng) => {
    return lat !== null && lng !== null &&
      lat !== undefined && lng !== undefined &&
      !isNaN(lat) && !isNaN(lng) &&
      lat !== 0 && lng !== 0;
  };

  // Filter trees with valid coordinates for map display
  const getValidTreesForMap = () => {
    return treeData.filter(tree => hasValidCoordinates(tree.lat, tree.lng));
  };

  const buildCumulativeCO2ChartData = (trees) => {
    if (!trees || trees.length === 0) return null;

    const now = new Date();

    // ── Find global start month ───────────────────────────────────────────────
    let globalStart = null;
    trees.forEach(tree => {
      const d = tree.plantedOn || tree.createdAt;
      if (!d) return;
      const date = new Date(d);
      if (!globalStart || date < globalStart) globalStart = date;
    });

    if (!globalStart) return null;

    const start = new Date(globalStart.getFullYear(), globalStart.getMonth(), 1);

    // Build month labels: start → today
    const labels = [];
    let cursor = new Date(start);
    while (cursor <= now) {
      labels.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    // ── For each month, sum CO2e of ALL trees that exist at that point ────────
    // Each tree's DBH grows at +0.05cm/month from its planting date.
    // If a real DBH measurement exists, use it to calibrate the growth:
    //   - interpolate from seedling→knownDBH over the period planting→today
    //   - then continue growing at +0.05cm/month after today

    const data = labels.map(label => {
      const [yr, mo] = label.split("-").map(Number);
      const monthDate = new Date(yr, mo - 1, 1);

      let monthTotal = 0;

      trees.forEach(tree => {
        const treeStartDate = tree.plantedOn || tree.createdAt;
        if (!treeStartDate) return;

        const treeStart = new Date(treeStartDate);
        const treeStartMonth = new Date(treeStart.getFullYear(), treeStart.getMonth(), 1);

        // Tree not planted yet in this month — skip
        if (monthDate < treeStartMonth) return;

        const knownDBH = tree.dia_breast_height != null &&
          tree.dia_breast_height !== "null" &&
          parseFloat(tree.dia_breast_height) > 0
          ? parseFloat(tree.dia_breast_height) : null;
        const wd = tree.bd_biomass_density;
        const ff = tree.form_factor;

        // Months this tree has been alive at this point in time
        const monthsAlive = (monthDate - treeStartMonth) / (1000 * 60 * 60 * 24 * 30.44);

        let dbhAtThisMonth;

        if (knownDBH) {
          // We know the DBH at ~today. Work backwards/forwards from that.
          // Assume linear growth from SEEDLING_DBH at planting → knownDBH at today
          const totalMonthsToNow = Math.max(1,
            (now - treeStartMonth) / (1000 * 60 * 60 * 24 * 30.44)
          );

          if (monthDate <= now) {
            // Past/present: interpolate seedling → knownDBH
            const progress = monthsAlive / totalMonthsToNow;
            dbhAtThisMonth = SEEDLING_DBH + progress * (knownDBH - SEEDLING_DBH);
          } else {
            // Future: grow from knownDBH at default rate
            const monthsPastNow = (monthDate - now) / (1000 * 60 * 60 * 24 * 30.44);
            dbhAtThisMonth = knownDBH + monthsPastNow * DBH_GROWTH_PER_MONTH;
          }
        } else {
          // No measurement — grow from seedling at +0.05cm/month
          dbhAtThisMonth = SEEDLING_DBH + monthsAlive * DBH_GROWTH_PER_MONTH;
        }

        dbhAtThisMonth = Math.max(SEEDLING_DBH, dbhAtThisMonth);
        monthTotal += calcCO2eFromDBH(dbhAtThisMonth, wd, ff);
      });
      return parseFloat(monthTotal.toFixed(2));
    });

    const totalCO2e = data[data.length - 1] || 0;
    const o2Data = data.map(v => parseFloat((v * CO2_TO_O2_RATIO).toFixed(2)));
    const totalO2 = o2Data[o2Data.length - 1] || 0;
    const treesWithDBH = trees.filter(t =>
      t.dia_breast_height != null &&
      t.dia_breast_height !== "null" &&
      parseFloat(t.dia_breast_height) > 0
    ).length;

    const dbhValues = trees
      .filter(t => t.dia_breast_height != null && t.dia_breast_height !== "null")
      .map(t => parseFloat(t.dia_breast_height))
      .sort((a, b) => b - a)
      .slice(0, 10);

    console.log("Top 10 DBH values:", dbhValues);
    console.log("Trees with DBH:", treesWithDBH, "of", trees.length);
    console.log("Sample tree CO2e at max DBH:", calcCO2eFromDBH(dbhValues[0], 0.5, 0.5));

    return { labels, data, o2Data, totalCO2e, totalO2, treesWithDBH, totalTrees: trees.length };
  };

  const co2ChartResult = buildCumulativeCO2ChartData(treeData);

  const co2ChartData = co2ChartResult ? {
    labels: co2ChartResult.labels,
    datasets: [
      {
        label: "Cumulative CO2e (kg)",
        data: co2ChartResult.data,
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        pointBackgroundColor: "#4CAF50",
      },
      {
        label: "O2 Produced (kg)",
        data: co2ChartResult.o2Data,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.08)",
        fill: true,
        tension: 0.3,
        borderDash: [4, 3],
        pointRadius: 2,
        pointBackgroundColor: "#3B82F6",
      },
    ]
  } : getDefaultChartData("CO2 Offset");

  const calculateAverageWeeklyTrees = (data) => {
    if (!data || data.length === 0) {
      return {
        labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        data: [0, 0, 0, 0, 0, 0, 0],
      };
    }

    const averageWeeklyTrees = {
      labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      data: [0, 0, 0, 0, 0, 0, 0],
    };

    const dayOfWeekMap = {
      0: 6,
      1: 0,
      2: 1,
      3: 2,
      4: 3,
      5: 4,
      6: 5,
    };

    const currentDate = new Date();
    const startDate = new Date();
    startDate.setDate(currentDate.getDate() - 7);

    const dayCount = Array(7).fill(0);

    data.forEach((tree) => {
      if (tree.plantedOn) {
        const plantedOn = new Date(tree.plantedOn);
        if (plantedOn >= startDate && plantedOn <= currentDate) {
          const dayOfWeek = plantedOn.getDay();
          const index = dayOfWeekMap[dayOfWeek];
          averageWeeklyTrees.data[index]++;
          dayCount[index]++;
        }
      }
    });

    averageWeeklyTrees.data = averageWeeklyTrees.data.map((count, index) => {
      return dayCount[index] === 0 ? 0 : count;
    });
    return averageWeeklyTrees;
  };

  // Fetch client data using existing API
  const fetchClientData = async () => {
    try {
      const response = await api.get(`/api/upload/clientData/${clientId}`);

      if (!response.data) {
        setError("No client data available.");
        return;
      }

      // Set client and company data (excluding tree data)
      setClientData(response.data.Userdata?.[0] || null);
      setCompanyData(response.data.Companydata?.[0] || null);
      setApiStats(response.data.TreeStats || []);

    } catch (error) {
      console.error("Error fetching client data:", error);
      setError("Failed to fetch client data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tree data using new streaming API
  const fetchTreeData = async () => {
    try {
      setTreeLoading(true);
      const response = await api.get(`/api/upload/client-data/${clientId}/trees-stream`);

      if (response.status != 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const trees = response.data; // Now it's a proper JS array

      // Process tree statistics
      const result = trees.reduce(
        (acc, tree) => {
          acc.totalTrees++;
          if (tree.isVerified === 1) {
            acc.verifiedTrees++;
          }
          return acc;
        },
        { totalTrees: 0, verifiedTrees: 0 }
      );

      // Calculate geolocated trees
      const validTrees = trees.filter(tree => hasValidCoordinates(tree.lat, tree.lng));
      result.geolocatedTrees = validTrees.length;

      setTreeData(trees);
      setTreeStats(result);
    } catch (error) {
      console.error("Error fetching tree data:", error);
      setError("Failed to fetch tree data. Please try again later.");
    } finally {
      setTreeLoading(false);
    }
  };


  useEffect(() => {
    if (clientId) {
      // Fetch both client data and tree data in parallel
      Promise.all([
        fetchClientData(),
        fetchTreeData()
      ]);
    } else {
      setError("No client ID provided.");
      setLoading(false);
      setTreeLoading(false);
    }
  }, [clientId]);

  const formatDateUs = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const options = { year: "numeric", month: "short" };
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      return "N/A";
    }
  };
  // Prepare chart data with fallbacks
  const months = apiStats.length > 0
    ? apiStats.map(stat => stat.registration_month)
    : ['No Data'];

  const treeStatsData = apiStats.length > 0 ? {
    labels: months,
    datasets: [{
      label: 'Total Trees Registered',
      borderColor: '#4CAF50',
      data: apiStats.map(stat => stat.total_trees_registered || 0),
      tension: 0.1
    }]
  } : getDefaultChartData('Total Trees Registered');

  const weeklyTreesData = calculateAverageWeeklyTrees(treeData);
  const averageWeeklyCount = weeklyTreesData.data.length > 0
    ? Math.round(weeklyTreesData.data.reduce((a, b) => a + b, 0) / 7)
    : 0;

  const validTreesForMap = getValidTreesForMap();
  const mapCenter = validTreesForMap.length > 0
    ? [validTreesForMap[0].lat, validTreesForMap[0].lng]
    : defaultPosition;

  // Show loading state while either client or tree data is loading
  if (loading || treeLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
        <div style={{ marginLeft: '20px' }}>
          <p>Loading client data...</p>
          {treeLoading && <p>Streaming tree data...</p>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <p style={{ color: '#f44336', fontSize: '18px' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="armasteel-dashboard">
      {isExporting && (
        <div className="pdf-export-overlay">
          <div className="pdf-loader-wrapper">
            <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
          </div>
          <p className="pdf-export-overlay-title">Generating Client PDF Report...</p>
          <p className="pdf-export-overlay-subtitle">Preparing charts, statistics, and species distribution details</p>
        </div>
      )}
      <section className="bg-image pt-40 pb-40">
        <div className="container">
          <h2
            className="wow fadeInUp"
            data-wow-duration="1.2s"
            data-wow-delay=".2s"
          >
            {/* Client Impact Dashboard :{" "}
            {companyData?.company_name || clientData?.full_name || "Unknown Client"} */}
            Company Climate Impact Report
          </h2>
        </div>
      </section>

      <div style={{ justifyContent: 'center' }}>
        {companyData?.company_cover_pic && (
          <div
            className="cover-image"
            style={{
              backgroundImage: `url(${companyData.company_cover_pic})`,
              height: "250px",
            }}
          ></div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', marginTop: '25px' }}>
        <div style={{ flex: 1 }}></div>
        {/* <div className="client-climate-report-heading" style={{ margin: '0' }}>
          Company Climate Impact Report
        </div> */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="export-pdf-btn"
            onClick={handleExportPDF}
            disabled={isExporting}
            style={{ margin: 0 }}
          >
            <FaFilePdf />{" "}
            <span>
              <strong style={{color:"#0c3292"}}>{isExporting ? "Exporting..." : "Export Report"}</strong>
            </span>
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card impact-profile">
          <h2>User Profile</h2>
          <div className="profile-content">
            {companyData?.company_logo ? (
              <img
                src={companyData.company_logo}
                alt="Company Logo"
                className="company-logo"
                style={{ objectFit: 'contain' }}
              />
            ) : (
              <div className="company-logo" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f0f0',
                color: '#666'
              }}>
                No Logo
              </div>
            )}

            <div className="company-info">
              <h3>
                {companyData?.company_name || clientData?.full_name || "Unknown Client"}
              </h3>
              <p>{companyData?.industry || "Industry not specified"}</p>
              <br />
              <p>Since: {formatDateUs(clientData?.createdAt)}</p>
            </div>
          </div>

          <div className="share-buttons">
            <FacebookShareButton url={window.location.href}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={window.location.href}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    const shareUrl = `https://www.instagram.com/`;
                    window.open(shareUrl, "_blank");
                  })
                  .catch((err) => {
                    console.error("Failed to copy: ", err);
                  });
              }}
              style={{
                height: "32px",
                width: "32px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                padding: "5px",
                backgroundColor: "#D93F7C",
              }}
            >
              <FaInstagram size={32} />
            </button>
            <WhatsappShareButton url={window.location.href}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={window.location.href}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <EmailShareButton url={window.location.href}>
              <EmailIcon size={32} round />
            </EmailShareButton>
          </div>
        </div>

        <div className="dashboard-card total-trees">
          <h2>Total Trees</h2>
          <div className="value">{treeData.length || 0}</div>
          <Line
            ref={totalTreesChartRef}
            style={{ width: "60%", height: "60%", marginTop: "10px" }}
            data={treeStatsData}
            options={{
              plugins: { legend: { display: false } },
              scales: { y: { display: false }, x: { display: false } },
            }}
          />
        </div>

        <div className="dashboard-card plantation-status">
          <h2>Plantation Status</h2>
          <div className="status-circle">
            <Doughnut
              ref={plantationStatusChartRef}
              data={{
                labels: ["Planted", "Verified", "Geolocated"],
                datasets: [
                  {
                    data: [
                      treeStats?.totalTrees || 0,
                      treeStats?.verifiedTrees || 0,
                      validTreesForMap.length || 0,
                    ],
                    backgroundColor: ["#4CAF50", "#FFA000", "#d30000"],
                  },
                ],
              }}
              options={{
                cutout: "85%",
                plugins: { legend: { display: false } },
              }}
            />
            <div className="status-text">
              <div className="value">{Math.round((treeData.length || 0) / 1000)}K</div>
              <div>Planted</div>
            </div>
          </div>
          {/* <div className="status-legend">
            <div className="legend-item">
              <span className="dot planted"></span> Planted ({treeStats?.totalTrees || 0})
            </div>
            <div className="legend-item">
              <span className="dot verified"></span> Verified ({treeStats?.verifiedTrees || 0})
            </div>
            <div className="legend-item">
              <span className="dot geolocated"></span> Geolocated ({validTreesForMap.length || 0})
            </div>
          </div> */}
        </div>

        <div className="dashboard-card average-weekly-trees">
          <h2>Average Weekly Trees</h2>
          <div className="value">{averageWeeklyCount}</div>
          <Bar
            ref={weeklyTreesChartRef}
            data={{
              labels: weeklyTreesData.labels,
              datasets: [
                {
                  label: "Trees Planted",
                  data: weeklyTreesData.data,
                  backgroundColor: "#4CAF50",
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Number of Trees",
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: "Day of Week",
                  },
                },
              },
            }}
          />
        </div>

        <div className="dashboard-card trees-by-month">
          <h2>Total Trees by Month</h2>
          <Line ref={treesByMonthChartRef} data={treeStatsData} />
        </div>

        <div className="dashboard-card co2-offset-by-month">
          <h2>Total Cumulative CO2 Offset &amp; O2 Produced by Month</h2>

          {co2ChartResult ? (
            <>
              <div style={{ display: 'flex', gap: '24px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total CO2e Stored </p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#4CAF50' }}>
                    {co2ChartResult.totalCO2e >= 1000
                      ? `${(co2ChartResult.totalCO2e / 1000).toFixed(2)} t`
                      : `${co2ChartResult.totalCO2e} kg`}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total O2 Produced </p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#3B82F6' }}>
                    {co2ChartResult.totalO2 >= 1000
                      ? `${(co2ChartResult.totalO2 / 1000).toFixed(2)} t`
                      : `${co2ChartResult.totalO2} kg`}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Trees with DBH data </p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#333' }}>
                    {co2ChartResult.treesWithDBH}
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                      {' '}/ {co2ChartResult.totalTrees} total
                    </span>
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-block' }} />
                  <span style={{ fontSize: '11px', color: '#888' }}>CO2e Absorbed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3B82F6', display: 'inline-block' }} />
                  <span style={{ fontSize: '11px', color: '#888' }}>O2 Produced</span>
                </div>
              </div>

              <Line
                ref={co2ChartRef}
                data={co2ChartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          const val = ctx.parsed.y;
                          const unit = val >= 1000 ? `${(val / 1000).toFixed(2)} t` : `${val} kg`;
                          return ` ${ctx.dataset.label.replace(' (kg)', '')}: ${unit}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "kg" }
                    },
                    x: {
                      title: { display: true, text: "Month" },
                      ticks: { maxTicksLimit: 12 }
                    }
                  }
                }}
              />

              <p style={{ fontSize: '10px', color: '#aaa', marginTop: '8px' }}>
                Biomass Formula Method (Chave et al. 2014) · Trees without DBH estimated at +0.05 cm/month growth · O2 = CO2e × 0.727
              </p>
            </>
          ) : (
            <>
              <Line
                data={getDefaultChartData("CO2 Offset")}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
              />
              <p style={{ fontSize: '11px', color: '#aaa', textAlign: 'center', marginTop: '8px' }}>
                No planting data available yet
              </p>
            </>
          )}
        </div>

        <div className="dashboard-card project-map">
          <h2>Project Locations & Tree Tracking</h2>
          {/* {validTreesForMap.length === 0 && (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              marginBottom: '10px',
              color: '#856404'
            }}>
              <strong>Note:</strong> {treeData.length > 0 
                ? `${treeData.length - validTreesForMap.length} trees are not displayed on the map due to missing location data.` 
                : "No trees with valid location data found."}
            </div>
          )} */}

          <MapContainer
            center={mapCenter}
            zoom={validTreesForMap.length > 0 ? 6 : 4}
            style={{ height: "50vh", width: "100%" }}
          >
            <TileLayer url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" />
            <MapToggle />

            {validTreesForMap.length > 0 && (
              <MarkerClusterGroup>
                {validTreesForMap.map((tree) => (
                  <Marker
                    key={tree.id}
                    position={[tree.lat, tree.lng]}
                    icon={tree.isVerified ? verifiedIcon : unverifiedIcon}
                  >
                    <Popup>
                      <div style={{
                        display: 'flex',
                        minWidth: '400px',
                        maxWidth: '600px',
                        gap: '4px',
                        padding: '0'
                      }}>
                        <div style={{ width: '40%' }}>
                          <a
                            target="_blank"
                            className="search-more-info-btn"
                            href={`/plantprofile/${tree.id}`}
                            rel="noreferrer"
                            title="More Info"
                          >
                            <img
                              src={tree.treeimageUrl || '/images/default-tree.png'}
                              alt={tree.imageName || 'Tree Image'}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'scale-down',
                                borderRadius: '4px'
                              }}
                            />
                          </a>
                        </div>
                        <div style={{
                          width: '40%',
                          fontSize: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            <a
                              target="_blank"
                              className="search-more-info-btn"
                              href={`/plantprofile/${tree.id}`}
                              rel="noreferrer"

                              title="More Info"
                            >
                              <p style={{
                                margin: 0,
                                lineHeight: '1'
                              }}>{tree.treeName || 'Unknown Tree'}</p>
                            </a>
                            {tree.isVerified ? (
                              <FaCheckCircle style={{
                                color: '#16a34a',
                                fontSize: '12px'
                              }} />
                            ) : (
                              <FaCheckCircle style={{
                                color: 'yellow',
                                fontSize: '12px'
                              }} />
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1px' }}>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="TreeId">
                              <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}>
                                <FaTree style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {tree.tree_id || 'N/A'}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded By">
                              <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}>
                                <FaUserAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {tree.full_name || 'Unknown'}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded On">
                              <span style={{ fontWeight: 600, color: 'black' }}>
                                <FaCalendarPlus style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {tree.createdAt ? new Date(tree.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Planted On">
                              <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}>
                                <FaCalendarAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {tree.plantedOn ? new Date(tree.plantedOn).toLocaleDateString() : 'N/A'}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Species">
                              <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}>
                                <FaPagelines style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {getSpeciesLabel(tree)}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Age">
                              <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}>
                                <FaClock style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span>{' '}
                              {tree.age || 'N/A'}
                            </p>
                            <p style={{
                              margin: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              wordBreak: 'break-word',
                              fontSize: '10px',
                              lineHeight: '1',
                              padding: '0'
                            }} title="Location">
                              <span style={{ fontWeight: 600, color: 'black' }}>
                                <FaMapMarkerAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />
                              </span> {tree.address || 'Location not specified'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
};
